/**
 * DesignerContext — the single shared context every DesignerTool
 * reads via `useDesigner()`.
 *
 * Phase 13 P0 (June 2026)
 * =======================
 *
 * Why a context, not props
 * ------------------------
 *
 * Tools live in slots that are rendered by `DesignerShell`, not by
 * the page that knows about the scene. Threading
 * `{scene, mutate, selection, ...}` through `<TopToolbar>`,
 * `<RightInspector>`, etc., would invert the framework's premise
 * (tools are loaded by the framework, not by the page). Context is
 * the right shape — every tool's Component calls `useDesigner()`
 * and receives the live state.
 *
 * Why a thin facade
 * -----------------
 *
 * The actual designer state lives in `sceneStore` (zustand + zundo)
 * for two reasons that pre-date Phase 13:
 *
 *   1. zundo gives us free undo/redo with a published API.
 *   2. zustand selectors let canvas components avoid re-rendering
 *      on unrelated state changes (selecting an actor doesn't
 *      re-render the timeline strip).
 *
 * Tearing both out would be a regression. So `DesignerContext`
 * exposes a curated, framework-shaped view of `sceneStore`'s
 * surface (the canonical reads + the mutators tools need) while
 * leaving the store itself in place for fine-grained reactivity.
 * Tools that need to subscribe to a single field directly (e.g.
 * the canvas overlay watching `selectedActorId`) can still
 * `useSceneStore(s => s.selectedActorId)` — `useDesigner()` is
 * additive.
 *
 * Context shape
 * -------------
 *
 *     {
 *       // Identity
 *       appSurface:   'coach' | 'learner' | 'platform_admin'
 *       userRoles:    string[]
 *       domainPack:   { id, label, scene_types, ... } | null
 *       // Scene
 *       scene:        SceneDoc | null
 *       sceneType:    string | null            // convenience for predicates
 *       capabilities: Set<string>              // resolved at provider mount
 *       format:       string | null            // Phase-12 facet
 *       subject:      string | null            // Phase-12 facet
 *       // Selection
 *       selectedActorId: string | null
 *       selectedEventId: string | null
 *       selectedEventIndex: number
 *       // Mutators
 *       mutateScene:  (patch | recipe) => void
 *       selectActor:  (actorId | null) => void
 *       selectEvent:  (eventIndex) => void
 *       moveActor:    (actorId, x, y) => void
 *       setMeta:      (patch) => void
 *       setDomainMetadata: (patch) => void
 *       // History
 *       undo:         () => void
 *       redo:         () => void
 *       canUndo:      boolean
 *       canRedo:      boolean
 *       // Cross-tool comm
 *       eventBus:     { emit(name, payload), on(name, fn), off(name, fn) }
 *     }
 *
 * The shape is closed (frozen at provider mount) so tools can
 * destructure freely; new fields require a Phase-13 minor (P0.x)
 * to add.
 */

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
} from 'react';

import { useSceneStore } from '../store/sceneStore';

// ────────────────────────────────────────────────────────────────────
// Lightweight event bus
// ────────────────────────────────────────────────────────────────────
//
// Used by tools to talk to each other without coupling: e.g. the
// telestration toolbar emits `telestration.tool_changed` and any
// canvas overlay that cares listens. Kept tiny on purpose — three
// methods, no async, no priority queues. Heavier coordination
// belongs in sceneStore.

function createEventBus() {
  /** @type {Map<string, Set<Function>>} */
  const handlers = new Map();
  return {
    on(name, fn) {
      if (!handlers.has(name)) handlers.set(name, new Set());
      handlers.get(name).add(fn);
      return () => handlers.get(name)?.delete(fn);
    },
    off(name, fn) {
      handlers.get(name)?.delete(fn);
    },
    emit(name, payload) {
      const set = handlers.get(name);
      if (!set) return;
      for (const fn of set) {
        try {
          fn(payload);
        } catch (err) {
          // eslint-disable-next-line no-console
          console.warn(`[designer.eventBus] handler for ${name} threw:`, err);
        }
      }
    },
    _handlers_for_test: handlers,
  };
}

// ────────────────────────────────────────────────────────────────────
// React context
// ────────────────────────────────────────────────────────────────────

const DesignerContext = createContext(null);

/**
 * `DesignerProvider` — mounts at the root of every designer surface
 * (one per page in the legacy world; a single mount under the
 * shell when Phase 13 P4 lands the cutover).
 *
 * Required props
 * --------------
 *
 *   * `appSurface`   'coach' | 'learner' | 'platform_admin'
 *   * `userRoles`    Array of role-id strings (from auth claims).
 *
 * Optional props
 * --------------
 *
 *   * `domainPack`   The resolved domain pack object.
 *   * `eventBus`     Inject an external event bus (mostly for tests).
 *
 * Children include the `DesignerShell` and any tool components.
 */
export function DesignerProvider({
  appSurface,
  userRoles = [],
  domainPack = null,
  eventBus: injectedBus = null,
  children,
}) {
  // Subscribe to the parts of sceneStore the context exposes.
  // We deliberately READ via the store's getState() in a memoized
  // selector rather than subscribe to the full state, so the
  // provider re-renders only when the relevant slice changes.
  const scene              = useSceneStore((s) => s.scene);
  const selectedActorId    = useSceneStore((s) => s.selectedActorId);
  const selectedEventId    = useSceneStore((s) => s.selectedEventId);
  const selectedEventIndex = useSceneStore((s) => s.selectedEventIndex);

  // Mutators — stable references straight from the store.
  const setMeta            = useSceneStore((s) => s.setMeta);
  const setDomainMetadata  = useSceneStore((s) => s.setDomainMetadata);
  const selectActor        = useSceneStore((s) => s.selectActor);
  const selectEvent        = useSceneStore((s) => s.selectEvent);
  const moveActor          = useSceneStore((s) => s.moveActor);

  // Undo / redo via zundo's temporal API.
  const undo = useCallback(() => {
    try { useSceneStore.temporal.getState().undo(); } catch (_) { /* not wired */ }
  }, []);
  const redo = useCallback(() => {
    try { useSceneStore.temporal.getState().redo(); } catch (_) { /* not wired */ }
  }, []);
  const canUndo = useSceneStore.temporal
    ? Boolean(useSceneStore.temporal.getState?.().pastStates?.length)
    : false;
  const canRedo = useSceneStore.temporal
    ? Boolean(useSceneStore.temporal.getState?.().futureStates?.length)
    : false;

  // Stable event bus per provider mount.
  const busRef = useRef(injectedBus || createEventBus());

  // Convenience field — many tools want to invoke ad-hoc patches
  // (e.g. the metadata form's "save title" button). `mutateScene`
  // accepts either a partial patch or a recipe (curried function
  // applied to the current scene), mirroring zustand's idiom.
  const mutateScene = useCallback((patchOrRecipe) => {
    useSceneStore.setState((s) => {
      if (!s.scene) return s;
      const next = typeof patchOrRecipe === 'function'
        ? patchOrRecipe(s.scene)
        : { ...s.scene, ...patchOrRecipe };
      return { ...s, scene: next, isDirty: true };
    });
  }, []);

  // Resolve Phase-12 facets + capability set ONCE per scene mutation
  // — every tool predicate reads them, so memoizing here saves the
  // O(tools × predicates) re-resolution per render.
  const { capabilities, sceneType, format, subject } = useMemo(() => {
    const meta = scene?.domain_metadata || {};
    // Start from the stored capability_snapshot, then auto-augment with
    // capabilities that can be inferred directly from scene structure.
    // This handles scenes that were seeded before capability_snapshot was
    // populated (many football drill/play scenes have actors but an empty
    // snapshot, which caused the 2D/3D toggle and spotlight tools to be
    // hidden).
    const caps = new Set(scene?.capability_snapshot || []);

    // Infer has_actors when the scene has at least one actor entry.
    if ((scene?.actors?.length ?? 0) > 0) caps.add('has_actors');
    // Infer has_timeline when the scene has at least one timeline event.
    if ((scene?.timeline?.length ?? 0) > 0) caps.add('has_timeline');

    return {
      capabilities: caps,
      sceneType:    scene?.scene_type || null,
      format:       meta.quiz_format || null,
      subject:      meta.quiz_subject || null,
    };
  }, [scene]);

  const value = useMemo(() => Object.freeze({
    // Identity
    appSurface,
    userRoles,
    domainPack,
    // Scene
    scene,
    sceneType,
    capabilities,
    format,
    subject,
    // Selection
    selectedActorId,
    selectedEventId,
    selectedEventIndex,
    // Mutators
    mutateScene,
    selectActor,
    selectEvent,
    moveActor,
    setMeta,
    setDomainMetadata,
    // History
    undo,
    redo,
    canUndo,
    canRedo,
    // Cross-tool comm
    eventBus: busRef.current,
  }), [
    appSurface, userRoles, domainPack,
    scene, sceneType, capabilities, format, subject,
    selectedActorId, selectedEventId, selectedEventIndex,
    mutateScene, selectActor, selectEvent, moveActor,
    setMeta, setDomainMetadata,
    undo, redo, canUndo, canRedo,
  ]);

  // Surface bus reference for legacy-page bridges (e.g. SessionPage
  // can listen to designer events without importing the bus directly).
  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.__flowgenix_designer_bus__ = busRef.current;
    }
    return () => {
      if (typeof window !== 'undefined' && window.__flowgenix_designer_bus__ === busRef.current) {
        delete window.__flowgenix_designer_bus__;
      }
    };
  }, []);

  return (
    <DesignerContext.Provider value={value}>
      {children}
    </DesignerContext.Provider>
  );
}

// ────────────────────────────────────────────────────────────────────
// Hook
// ────────────────────────────────────────────────────────────────────

/**
 * `useDesigner()` — the canonical read API for tool components.
 *
 * Throws if called outside a `<DesignerProvider>`. We deliberately
 * DO NOT provide a "safe fallback" here — a tool mounted outside
 * the shell is a bug, and silently returning a stub would mask
 * the bug behind a confusing rendering glitch.
 */
export function useDesigner() {
  const ctx = useContext(DesignerContext);
  if (ctx === null) {
    throw new Error(
      'useDesigner() must be called from a component mounted inside ' +
      '<DesignerProvider>. Did you forget to wrap your designer page?',
    );
  }
  return ctx;
}

/**
 * `useDesignerSafe()` — non-throwing variant for components that
 * legitimately render both inside and outside the shell (e.g. the
 * Phase 12 InteractiveQuizPlayer, which runs in both designer
 * preview AND learner playback). Returns `null` outside a provider.
 */
export function useDesignerSafe() {
  return useContext(DesignerContext);
}

// Internal — used by tests to construct a minimal context object
// without spinning up zustand / zundo.
export const _DesignerContextForTests = DesignerContext;
